import { useEffect, useState } from 'react';
import { AppSettings } from './Configuration/types';
import { store } from './store';
import { initializeSettings } from './Configuration/AppSettings';
import { useErrorBoundary } from 'react-error-boundary';
import Application from './Application';
import AppInsights from './AppInsights/AppInsights';
import AuthenticationProvider from './Security/AuthenticationProvider';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import TriniumUserGate from './Security/TriniumUserGate';
import CompanyProgressBar from './Components/Loading/CompanyProgressBar';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

interface ApplicationInitializerProps {
	appSettings: AppSettings;
	appInsightsInstance?: ApplicationInsights;
	reactPlugin: ReactPlugin;
}

/**
 * Component responsible for the Application starting up. Handles immediate responsibilities
 * of dispatching the appSettings, instantiating App Insights, MUI Theme, Authentication, etc.
 */
export default function ApplicationInitializer(props: ApplicationInitializerProps) {
	const [initializing, setInitializing] = useState(true);
	const { appSettings } = props;
	const { showBoundary } = useErrorBoundary();

	useEffect(() => {
		try {
			store.dispatch(initializeSettings(appSettings));
		} catch (ex) {
			showBoundary(ex);
		} finally {
			setInitializing(false);
		}
	}, [appSettings, showBoundary]);

	if (initializing) {
		return <CompanyProgressBar />;
	}

	return (
		<AppInsights
			appInsightsInstance={props.appInsightsInstance}
			reactPlugin={props.reactPlugin}
		>
			<AuthenticationProvider>
				<TriniumUserGate>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Application />
					</LocalizationProvider>
				</TriniumUserGate>
			</AuthenticationProvider>
		</AppInsights>
	);
}
